/** Assumed download throughput used for the client-side estimate. */
export const DOWNLOAD_SPEED_MBIT_PER_SECOND = 45;

const BYTES_PER_MB = 1024 * 1024;
const BYTES_PER_GB = 1024 * BYTES_PER_MB;

/** Whole minutes (rounded up) to download `sizeBytes` at 45 Mbit/s; `null` when the size is unknown. */
export function estimateDownloadMinutes(sizeBytes: number | null | undefined): number | null {
  if (sizeBytes === null || sizeBytes === undefined || !Number.isFinite(sizeBytes) || sizeBytes <= 0) {
    return null;
  }
  const seconds = (sizeBytes * 8) / (DOWNLOAD_SPEED_MBIT_PER_SECOND * 1_000_000);
  return Math.max(1, Math.ceil(seconds / 60));
}

/** Compact size text (MB below 1 GB, otherwise GB); empty when unknown. Units are language-neutral. */
export function formatArchiveSize(sizeBytes: number | null | undefined): string {
  if (sizeBytes === null || sizeBytes === undefined || !Number.isFinite(sizeBytes) || sizeBytes <= 0) {
    return '';
  }
  if (sizeBytes >= BYTES_PER_GB) {
    return `${(sizeBytes / BYTES_PER_GB).toFixed(2)} GB`;
  }
  return `${Math.round(sizeBytes / BYTES_PER_MB)} MB`;
}

export function formatCloudCover(cloudCover: number | null | undefined): string {
  if (cloudCover === null || cloudCover === undefined || !Number.isFinite(cloudCover)) {
    return '';
  }
  return `${Math.round(cloudCover * 10) / 10} %`;
}

/**
 * Makes a backend-relative preview URL usable by `<img src>`. The backend returns `/api/...` paths while
 * `apiBase` (`environment.api`) is the base the rest of the app prepends to route paths, so the leading
 * `/api` is dropped before joining.
 */
export function resolvePreviewUrl(previewUrl: string | null | undefined, apiBase: string): string | null {
  if (!previewUrl) {
    return null;
  }
  if (/^https?:\/\//i.test(previewUrl)) {
    return previewUrl;
  }
  const base = apiBase.replace(/\/+$/, '');
  const path = previewUrl.startsWith('/') ? previewUrl : `/${previewUrl}`;
  return `${base}${path.replace(/^\/api(?=\/)/, '')}`;
}
